import { useLocation } from "react-router-dom";
import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { CSSRulePlugin } from "gsap/all";
import { MorphSVGPlugin } from "gsap/MorphSVGPlugin";

import { useSpline } from "../../../context/SplineContext";
import BgAnim from "../../../components/BgAnimShape";

import img1 from "../../../assets/awaken-img-1.png";
import img2 from "../../../assets/awaken-img-2.png";
import img3 from "../../../assets/awaken-img-3.svg";
import img4 from "../../../assets/awaken-img-4.png";
import img5 from "../../../assets/awaken-img-5.png";
import img6 from "../../../assets/awaken-img-6.png";
import img7 from "../../../assets/awaken-img-7.png";
import img8 from "../../../assets/awaken-img-8.png";
import img9 from "../../../assets/awaken-img-9.svg";
import img10 from "../../../assets/awaken-img-10.png";
import shapeImg from "../../../assets/awaken-shape.svg";
import awakenBg from "../../../assets/awaken-bg.svg";
import brightenBg from "../../../assets/brighten-bg.svg";
import brightenImg1 from "../../../assets/brighten-img-1.svg";
import brightenImg2 from "../../../assets/brighten-img-2.svg";
import brightenImg3 from "../../../assets/brighten-img-3.svg";
import brightenImg4 from "../../../assets/brighten-img-4.svg";
import brightenImg5 from "../../../assets/brighten-img-5.svg";
import brightenImg6 from "../../../assets/brighten-img-6.svg";
import brightenImg7 from "../../../assets/brighten-img-7.svg";
import crystalliseBg from "../../../assets/crystallise-bg.svg";

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(CSSRulePlugin);
gsap.registerPlugin(MorphSVGPlugin);

export default function AwakenSection({ splineReg, onSplineReady }) {
  const {
    splineObjectRef,
    isSplineReady,
    stateOneRef,
    stateTwoRef,
    groupScaleRef,
  } = useSpline();

  const location = useLocation();

  const isHomepage = location.pathname === "/";
  const sectionRef = useRef(null);

  const mobile = window.matchMedia("(max-width: 768px)").matches;

  useEffect(() => {
    if (!isHomepage || !isSplineReady || !splineObjectRef.current) return;
    const obj = splineObjectRef.current;
    const firstState = stateOneRef.current;
    const secondState = stateTwoRef.current;
    const groupScale = groupScaleRef.current;

    const section = sectionRef.current;
    const imageFirst = document.querySelector(".awaken-assets__1");
    const secondImage = document.querySelector(".awaken-assets__2");
    const thirdImage = document.querySelector(".awaken-assets__3");
    const fourthImage = document.querySelector(".awaken-assets__4");
    const fiveImage = document.querySelector(".awaken-assets__5");
    const sixImage = document.querySelector(".awaken-assets__6");
    const seventhImage = document.querySelector(".awaken-assets__7");
    const eightImage = document.querySelector(".awaken-assets__8");
    const nineImage = document.querySelector(".awaken-assets__9");
    const tenImage = document.querySelector(".awaken-assets__10");

    const brightenImgFirst = document.querySelector(".brighten-assets__1");
    const brightenImgSecond = document.querySelector(".brighten-assets__2");
    const brightenImgThird = document.querySelector(".brighten-assets__3");
    const brightenImgFourth = document.querySelector(".brighten-assets__4");
    const brightenImgFifth = document.querySelector(".brighten-assets__5");
    const brightenImgSixth = document.querySelector(".brighten-assets__6");
    const brightenImgSeventh = document.querySelector(".brighten-assets__7");
    const brightenImgEight = document.querySelector(".brighten-assets__8");
    const brightenImgNineth = document.querySelector(".brighten-assets__9");
    const brightenImgTenth = document.querySelector(".brighten-assets__10");

    const awarnesHeading = document.querySelector(".heading-awaken");
    const brightenHeading = document.querySelector(".heading-brighten");
    const crystalliseHeading = document.querySelector(".heading-crystallise");

    const awarnesBg = document.querySelector(".awaken-bg-img");
    const brightenBg = document.querySelector(".brigten-bg");
    const crystalliseBg = document.querySelector(".crystallise-bg");

    let hasTriggeredFirst = false;
    let hasTriggeredSecond = false;

    const ctx = gsap.context(() => {
      const tlAwaken = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: "bottom bottom",
          scrub: 1,
          onEnter: () => {
            if (location.pathname !== "/") return;
            if (!hasTriggeredFirst) {
              gsap.set(firstState.rotation, { x: 0 }); // resetuj vrednost da bi se animacija desila
              gsap.timeline({ overwrite: true }).to(firstState.rotation, {
                x: 2,
                duration: 1,
                overwrite: true,
              });
              hasTriggeredFirst = true;
              hasTriggeredSecond = false;
            }
          },
          onLeaveBack: () => {
            if (!hasTriggeredSecond) {
              if (location.pathname !== "/") return;
              gsap.set(secondState.rotation, { x: 0 }); // resetuj
              gsap.timeline({ overwrite: true }).to(secondState.rotation, {
                x: 2,
                duration: 1,
                overwrite: true,
              });
              hasTriggeredSecond = true;
              hasTriggeredFirst = false;
            }
          },
        },
      });

      window.awakenTimeline = tlAwaken;

      if (mobile) {
        tlAwaken
          .to(groupScale.scale, {
            x: 10,
            y: 10,
            z: 10,
            duration: 1.5,
          })
          .to(
            imageFirst,
            {
              opacity: 1,
              transform: "translate(-42rem, -34rem) scale(1.5) rotate(5deg)",
            },
            "-=1.5"
          )
          .to(
            secondImage,
            {
              opacity: 1,
              transform: "translate(-1rem, -40rem) scale(1.4) rotate(-7deg)",
            },
            "-=1.5"
          )
          .to(
            thirdImage,
            {
              opacity: 1,
              transform: "translate(44rem, -35rem) scale(1.3) rotate(18deg)",
            },
            "-=1.5"
          )
          .to(
            fourthImage,
            {
              opacity: 1,
              transform: "translate(31rem, 31rem) scale(1.5) rotate(-7deg)",
            },
            "-=1.4"
          )
          .to(
            ".home-bg-wrapper",
            {
              opacity: 1,
              duration: 0.5,
            },
            "-=1.4"
          )
          .to(
            seventhImage,
            {
              opacity: 1,
              transform: "translate(-11rem, 47rem) scale(1.4) rotate(6deg)",
            },
            "-=1.5"
          )
          .to(
            nineImage,
            {
              opacity: 1,
              transform: "translate(-34rem, 24rem) rotate(-27deg)",
            },
            "-=1.5"
          )
          .to(
            tenImage,
            {
              opacity: 1,
              transform: "translate(-76rem, 0rem) scale(3) rotate(7deg)",
            },
            "-=1.4"
          )
          .to(
            imageFirst,
            {
              transform: "translate(-47rem, -70vh) rotate(25deg) scale(3)",
              duration: 1,
            },
            "-=1"
          )
          .to(
            secondImage,
            {
              transform: "translate(53rem, -68vh) rotate(-16deg) scale(3.2)",
              filter: "blur(1px)",
              duration: 1,
            },
            "-=.9"
          )
          .to(
            thirdImage,
            {
              transform: "translate(67rem, -46rem) rotate(18deg) scale(2)",
              opacity: 0,
              duration: 1,
            },
            "-=1"
          )
          .to(
            fourthImage,
            {
              transform:
                "translate3d(81rem, 56rem, 0px) rotate(-16deg) scale(3)",
              duration: 1,
            },
            "-=1"
          )
          .to(
            fiveImage,
            {
              opacity: "1",
              scale: 1.5,
              duration: 1,
            },
            "-=1"
          )
          .to(
            seventhImage,
            {
              opacity: "0",
              transform: "translate(-18rem, 72rem) scale(2.5) rotate(18deg)",
            },
            "-=.9"
          )
          .to(
            nineImage,
            {
              transform: "translate(-60rem, 38rem) rotate(-27deg) scale(1.5)",
              opacity: "0",
              duration: 1,
            },
            "-=1"
          )
          .to(
            sixImage,
            {
              opacity: "1",
              scale: 1.5,
              duration: 0.5,
            },
            "-=.7"
          )
          .to(
            eightImage,
            {
              opacity: "1",
              scale: 1.5,
              duration: 0.5,
            },
            "-=.5"
          )
          .to(sixImage, {
            opacity: "0",
            scale: 2,
            duration: 0.5,
          })
          .to(
            fiveImage,
            {
              opacity: "0",
              scale: 2,
              duration: 0.5,
            },
            "-=.3"
          )
          .to(
            eightImage,
            {
              duration: 0.5,
              opacity: "0",
              scale: 2,
            },
            "-=.2"
          )
          .to(awarnesHeading, {
            opacity: 0,
            duration: 0.5,
          })
          .to(
            awarnesBg,
            {
              opacity: 0,
              duration: 0.5,
            },
            "-=.5"
          )
          .to(
            brightenBg,
            {
              opacity: 1,
              duration: 0.5,
            },
            "-=.4"
          )
          .to(
            brightenHeading,
            {
              opacity: 1,
              duration: 0.5,
            },
            "-=.1"
          )
          .to(
            [
              brightenImgFirst,
              brightenImgSecond,
              brightenImgThird,
              brightenImgFifth,
            ],
            {
              opacity: 1,
              duration: 0.3,
            },
            "-=.2"
          )
          .to(
            [
              brightenImgFourth,
              brightenImgSixth,
              brightenImgSeventh,
              brightenImgEight,
              brightenImgNineth,
            ],
            {
              opacity: 1,
              duration: 0.3,
            }
          )
          .to(".start-path", {
            morphSVG: ".end-path",
            duration: 1,
            ease: "power2.inOut",
          })
          .to(
            ".first-part",
            {
              duration: 1.2,
              morphSVG: ".second-part",
              ease: "power1.inOut",
            },
            "-=1"
          )
          .fromTo(
            ".brighten-assets__1 svg rect",
            {
              width: "40%",
            },
            {
              width: "100%",
              duration: 1,
              ease: "power2.inOut",
            },
            "-=1"
          )
          .to(
            brightenImgEight,
            {
              rotate: "29deg",
              duration: 1,
            },
            "-=1"
          )
          .to(
            brightenImgNineth,
            {
              rotate: "27deg",
              duration: 1,
            },
            "-=1"
          )
          .to(
            brightenImgSixth,
            {
              rotate: "180",
              duration: 1,
            },
            "-=1"
          )
          .to(
            ".brighten-assets__5 img",
            {
              transform: "translate(0rem, -3rem) scale(2.9)",
              duration: 1,
            },
            "-=1"
          )
          .to(
            [
              brightenImgFirst,
              brightenImgSecond,
              brightenImgThird,
              brightenImgFifth,
              brightenImgTenth,
            ],
            {
              scale: 0.7,
              opacity: 0,
            }
          )
          .to(
            [
              brightenImgFourth,
              brightenImgSixth,
              brightenImgSeventh,
              brightenImgEight,
              brightenImgNineth,
            ],
            {
              scale: 0.7,
              opacity: 0,
            }
          )
          .to([brightenHeading, brightenBg], {
            opacity: 0,
            duration: 0.5,
          })
          .to(
            crystalliseBg,
            {
              opacity: 1,
              duration: 0.5,
            },
            "-=.3"
          )
          .to(crystalliseHeading, {
            opacity: 1,
            duration: 0.5,
          })
          .to(".crystallise-assets__1 span", {
            opacity: 1,
            scale: 1,
            duration: 0.3,
          })
          .to(
            ".crystallise-assets__2 span",
            {
              opacity: 1,
              scale: 1,
              duration: 0.3,
            },
            "-=.1"
          )
          .to(
            ".crystallise-assets__3 span",
            {
              opacity: 1,
              scale: 1,
              duration: 0.3,
            },
            "-=.1"
          )
          .to(
            ".crystallise-assets__4 span",
            {
              opacity: 1,
              scale: 1,
              duration: 0.3,
            },
            "-=.1"
          )
          .to(
            ".crystallise-assets__5 span",
            {
              opacity: 1,
              scale: 1,
              duration: 0.3,
            },
            "-=.1"
          )
          .to(
            ".crystallise-assets__6 span",
            {
              opacity: 1,
              scale: 1,
              duration: 0.3,
            },
            "-=.1"
          )
          .to(
            ".crystallise-assets__7 span",
            {
              opacity: 1,
              scale: 1,
              duration: 0.3,
            },
            "-=.1"
          )
          .to(".crystallise-assets__1 span", {
            x: "-38rem",
            y: "-28rem",
            scale: "1.1",
            rotate: "49deg",
            duration: 1,
          })
          .to(
            ".crystallise-assets__2 span",
            {
              x: "-35rem",
              y: "36rem",
              scale: "1.1",
              roate: "43deg",
              duration: 1,
            },
            "-=1"
          )
          .to(
            ".crystallise-assets__3 span",
            {
              x: "-15rem",
              y: "60rem",
              scale: "1.1",
              duration: 1,
            },
            "-=1"
          )
          .to(
            ".crystallise-assets__4 span",
            {
              x: "12rem",
              y: "51rem",
              rotate: "66deg",
              scale: "1.1",
              duration: 1,
            },
            "-=1"
          )
          .to(
            ".crystallise-assets__5 span",
            {
              x: "34rem",
              y: "32rem",
              scale: "1.1",
              rotate: "42deg",
              duration: 1,
            },
            "-=1"
          )
          .to(
            ".crystallise-assets__6 span",
            {
              x: "28rem",
              y: "-50rem",
              rotate: "-51deg",
              scale: "1.1",
              duration: 1,
            },
            "-=1"
          )
          .to(
            ".crystallise-assets__7 span",
            {
              x: "-16rem",
              y: "-45rem",
              rotate: "70deg",
              scale: "1.1",
              duration: 1,
            },
            "-=1"
          );
        // end mobile
      } else {
        tlAwaken
          .to(groupScale.scale, {
            x: 35,
            y: 35,
            z: 35,
            duration: 1.5,
          })
          .to(
            imageFirst,
            {
              opacity: 1,
              transform:
                "translate(-10.1rem, -11.8rem) rotate(5deg) scale(1.5)",
            },
            "-=1.5"
          )
          .to(
            secondImage,
            {
              opacity: 1,
              transform: "translate(12rem, -9rem) scale(1.3) rotate(-7deg)",
            },
            "-=1.5"
          )
          .to(
            thirdImage,
            {
              opacity: 1,
              transform: "translate(22rem, -4rem) scale(1.2) rotate(4deg)",
            },
            "-=1.5"
          )
          .to(
            fourthImage,
            {
              opacity: 1,
              transform: "translate(15rem, 9rem) scale(1.2) rotate(4deg)",
            },
            "-=1.4"
          )
          .to(
            ".home-bg-wrapper",
            {
              opacity: 1,
              duration: 0.5,
            },
            "-=1.4"
          )
          .to(
            seventhImage,
            {
              opacity: 1,
              transform: "translate(-4rem, 13rem) scale(1.5) rotate(6deg)",
            },
            "-=1.5"
          )
          .to(
            nineImage,
            {
              opacity: 1,
              transform: "translate(-22rem, 9rem) rotate(-27deg)",
            },
            "-=1.5"
          )
          .to(
            tenImage,
            {
              opacity: 1,
              transform: "translate(-27rem, -2rem) scale(1.8) rotate(-5deg)",
            },
            "-=1.4"
          )

          .to(
            imageFirst,
            {
              transform: "translate(-21rem, -70vh) scale(3) rotate(9deg)",
              duration: 1,
            },
            "-=1"
          )
          .to(
            secondImage,
            {
              transform: "translate(27rem, -70vh) rotate(-16deg) scale(3.5)",
              filter: "blur(1px)",
              duration: 1,
            },
            "-=.9"
          )
          .to(
            thirdImage,
            {
              transform: "translate(36rem, 1rem) rotate(31deg) scale(2)",
              opacity: 0,
              duration: 1,
            },
            "-=1"
          )
          .to(
            fourthImage,
            {
              transform: "translate(31rem, 80vh) rotate(-16deg) scale(4)",
              duration: 1,
            },
            "-=1"
          )
          .to(
            fiveImage,
            {
              opacity: "1",
              scale: 1.5,
              duration: 1,
            },
            "-=1"
          )
          .to(
            seventhImage,
            {
              transform: "translate(-9rem, 22rem) rotate(0) scale(3)",
              opacity: "0",
              duration: 1,
            },
            "-=1"
          )
          .to(
            nineImage,
            {
              transform: "translate(-40rem,14rem) rotate(-0) scale(1.5)",
              opacity: "0",
              duration: 1,
            },
            "-=1"
          )
          .to(
            tenImage,
            {
              duration: 1,
              transform: "translate(-63rem, -6rem) rotate(-12deg) scale(4.5)",
              filter: "blur(1px)",
              duration: 1,
            },
            "-=1"
          )
          .to(
            sixImage,
            {
              opacity: "1",
              scale: 1.5,
              duration: 0.5,
            },
            "-=.6"
          )
          .to(
            eightImage,
            {
              opacity: "1",
              scale: 1.5,
              duration: 0.5,
            },
            "-=.4"
          )
          .to(sixImage, {
            opacity: "0",
            scale: 2,
            duration: 0.5,
          })
          .to(
            fiveImage,
            {
              opacity: "0",
              scale: 2,
              duration: 0.5,
            },
            "-=.3"
          )
          .to(
            eightImage,
            {
              duration: 0.5,
              opacity: "0",
              scale: 2,
            },
            "-=.2"
          )
          .to(awarnesHeading, {
            opacity: 0,
            duration: 0.5,
          })
          .to(
            awarnesBg,
            {
              opacity: 0,
              duration: 0.5,
            },
            "-=.5"
          )
          .to(
            brightenBg,
            {
              opacity: 1,
              duration: 0.5,
            },
            "-=.3"
          )
          .to(brightenHeading, {
            opacity: 1,
            duration: 0.5,
          })
          .to(
            [
              brightenImgFirst,
              brightenImgSecond,
              brightenImgThird,
              brightenImgFifth,
            ],
            {
              opacity: 1,
              duration: 0.3,
            },
            "-=.2"
          )
          .to(
            [
              brightenImgFourth,
              brightenImgSixth,
              brightenImgSeventh,
              brightenImgEight,
              brightenImgNineth,
            ],
            {
              opacity: 1,
              duration: 0.3,
            }
          )
          .to(".start-path", {
            morphSVG: ".end-path",
            duration: 1,
            ease: "power2.inOut",
          })
          .to(
            ".first-part",
            {
              duration: 1.2,
              morphSVG: ".second-part",
              ease: "power1.inOut",
            },
            "-=1"
          )
          .fromTo(
            ".brighten-assets__1 svg rect",
            {
              width: "40%",
            },
            {
              width: "100%",
              duration: 1,
              ease: "power2.inOut",
            },
            "-=1"
          )
          .to(
            brightenImgEight,
            {
              rotate: "29deg",
              duration: 1,
            },
            "-=1"
          )
          .to(
            brightenImgNineth,
            {
              rotate: "27deg",
              duration: 1,
            },
            "-=1"
          )
          .to(
            brightenImgSixth,
            {
              rotate: "180",
              duration: 1,
            },
            "-=1"
          )
          .to(
            ".brighten-assets__5 img",
            {
              transform: "translate(0rem, -3rem) scale(2.9)",
              duration: 1,
            },
            "-=1"
          )
          .to(brightenImgTenth, {
            opacity: 1,
            duration: 1,
          })
          .to(
            [
              brightenImgFirst,
              brightenImgSecond,
              brightenImgThird,
              brightenImgFifth,
              brightenImgTenth,
            ],
            {
              scale: 0.7,
              opacity: 0,
            }
          )
          .to(
            [
              brightenImgFourth,
              brightenImgSixth,
              brightenImgSeventh,
              brightenImgEight,
              brightenImgNineth,
            ],
            {
              scale: 0.7,
              opacity: 0,
            }
          )
          .to([brightenHeading, brightenBg], {
            opacity: 0,
            duration: 0.5,
          })
          .to(
            crystalliseBg,
            {
              opacity: 1,
              duration: 0.5,
            },
            "-=.3"
          )
          .to(
            crystalliseHeading,
            {
              opacity: 1,
              duration: 0.5,
            },
            "-=.3"
          )
          .to(".crystallise-assets__1 span", {
            opacity: 1,
            scale: 1,
            duration: 0.3,
          })
          .to(
            ".crystallise-assets__2 span",
            {
              opacity: 1,
              scale: 1,
              duration: 0.3,
            },
            "-=.1"
          )
          .to(
            ".crystallise-assets__3 span",
            {
              opacity: 1,
              scale: 1,
              duration: 0.3,
            },
            "-=.1"
          )
          .to(
            ".crystallise-assets__4 span",
            {
              opacity: 1,
              scale: 1,
              duration: 0.3,
            },
            "-=.1"
          )
          .to(
            ".crystallise-assets__5 span",
            {
              opacity: 1,
              scale: 1,
              duration: 0.3,
            },
            "-=.1"
          )
          .to(
            ".crystallise-assets__6 span",
            {
              opacity: 1,
              scale: 1,
              duration: 0.3,
            },
            "-=.1"
          )
          .to(
            ".crystallise-assets__7 span",
            {
              opacity: 1,
              scale: 1,
              duration: 0.3,
            },
            "-=.1"
          )
          .to(
            ".crystallise-assets__1 span",
            {
              opacity: 1,
              scale: 1,
              duration: 0.3,
            },
            "-=.1"
          )
          .to(".crystallise-assets__1 span", {
            x: "-30rem",
            y: "-8rem",
            scale: "1.1",
            rotate: "16deg",
            duration: 1,
          })
          .to(
            ".crystallise-assets__2 span",
            {
              x: "-24rem",
              y: "12rem",
              rotate: "-25deg",
              scale: "1.1",
              duration: 1,
            },
            "-=1"
          )
          .to(
            ".crystallise-assets__3 span",
            {
              x: "-3rem",
              y: "17rem",
              rotate: "113deg",
              scale: "1.1",
              duration: 1,
            },
            "-=1"
          )
          .to(
            ".crystallise-assets__4 span",
            {
              x: "19rem",
              y: "18rem",
              rotate: "53deg",
              scale: "1.1",
              duration: 1,
            },
            "-=1"
          )
          .to(
            ".crystallise-assets__5 span",
            {
              x: "27rem",
              y: "7rem",
              rotate: "8deg",
              scale: "1.1",
              duration: 1,
            },
            "-=1"
          )
          .to(
            ".crystallise-assets__6 span",
            {
              x: "20rem",
              y: "-15rem",
              rotate: "38deg",
              scale: "1.1",
              duration: 1,
            },
            "-=1"
          )
          .to(
            ".crystallise-assets__7 span",
            {
              x: "-12rem",
              y: "-15rem",
              rotate: "51deg",
              scale: "1.1",
              duration: 1,
            },
            "-=1"
          );
      }
      return () => {
        ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
        ctx.revert();
      };
    }, sectionRef);
  }, [isHomepage, isSplineReady]);

  return (
    <section id="awaken" ref={sectionRef}>
      <h2 className="heading-awaken">Awaken</h2>
      <div className="awaken-wrapper">
        <div className="awaken-wrapper-in">
          <div className="awaken-heading">
            <h2 className="heading-brighten">Brighten</h2>
            <h2 className="heading-crystallise">
              <div className="bg-line"></div>
              Crystallise
            </h2>
          </div>
          <div className="awaken-bg">
            <img className="awaken-bg-img" src={awakenBg} alt="bg" />
            <img className="brigten-bg" src={brightenBg} alt="brighten" />
            <img
              className="crystallise-bg"
              src={crystalliseBg}
              alt="crystallise"
            />
          </div>
        </div>
        <div className="awaken-assets">
          <div className="awaken-assets__all awaken-assets__1">
            <img src={img1} alt="" />
          </div>
          <div className="awaken-assets__all awaken-assets__2">
            <img src={img2} alt="" />
          </div>
          <div className="awaken-assets__all awaken-assets__3">
            <img src={img3} alt="" />
          </div>
          <div className="awaken-assets__all awaken-assets__4">
            <img src={img4} alt="" />
          </div>
          <div className="awaken-assets__all awaken-assets__5">
            <img src={img5} alt="" />
          </div>
          <div className="awaken-assets__all awaken-assets__6">
            <img src={img6} alt="" />
          </div>
          <div className="awaken-assets__all awaken-assets__7">
            <img src={img7} alt="" />
          </div>
          <div className="awaken-assets__all awaken-assets__8">
            <img src={img8} alt="" />
          </div>
          <div className="awaken-assets__all awaken-assets__9">
            <img src={img9} alt="" />
          </div>
          <div className="awaken-assets__all awaken-assets__10">
            <img src={img10} alt="" />
          </div>
          <div className="brighten-assets">
            <div className="brighten-assets__all brighten-assets__1">
              <svg
                width="243"
                height="171"
                viewBox="0 0 243 171"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  width="243"
                  height="84"
                  rx="15"
                  transform="matrix(-1 8.74228e-08 8.74228e-08 1 243 0)"
                  fill="#8ABEAC"
                />
              </svg>
            </div>
            <div className="brighten-assets__all brighten-assets__2">
              <svg
                width="500"
                height="250"
                viewBox="0 0 500 250"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.201416 15.5892C0.201416 7.24069 6.96921 0.4729 15.3177 0.4729H53.1084C61.4569 0.4729 68.2247 7.24069 68.2247 15.5892V53.8837C68.2247 62.2322 61.4569 69 53.1084 69H15.3177C6.96922 69 0.201416 62.2322 0.201416 53.8838V15.5892Z"
                  fill="#FEBF88"
                  className="start-path"
                />
                <path
                  className="end-path"
                  style={{ visibility: "hidden" }}
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M448 49C448 37.9543 439.046 29 428 29H131C119.954 29 111 37.9543 111 49V94C111 105.046 102.046 114 91 114H52C40.9543 114 32 122.954 32 134V198C32 209.046 40.9543 218 52 218H116C127.046 218 136 209.046 136 198V153C136 141.954 144.954 133 156 133H428C439.046 133 448 124.046 448 113V49Z"
                  fill="#FEBF88"
                />
              </svg>
            </div>
            <div className="brighten-assets__all brighten-assets__3">
              <img src={brightenImg1} alt="shape" />
            </div>
            <div className="brighten-assets__all brighten-assets__4">
              <svg
                width="171"
                height="171"
                viewBox="0 0 171 171"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  className="first-part"
                  d="M0.242188 9.47034C0.242188 4.6359 4.16128 0.716797 8.99573 0.716797H120.246C125.081 0.716797 129 4.63589 129 9.47034V39.2461C129 44.0806 125.081 47.9997 120.246 47.9997H8.99573C4.16128 47.9997 0.242188 44.0806 0.242188 39.2461V9.47034Z"
                  fill="#FEBF88"
                />
                <path
                  style={{ visibility: "hidden" }}
                  className="second-part"
                  d="M30,0 H141 A30,30 0 0 1 171,30 V141 A30,30 0 0 1 141,171 H30 A30,30 0 0 1 0,141 V30 A30,30 0 0 1 30,0 Z"
                  fill="#FEBF88"
                />
              </svg>
            </div>
            <div className="brighten-assets__all brighten-assets__5">
              <img src={brightenImg7} alt="" />
            </div>
            <div className="brighten-assets__all brighten-assets__6">
              <img src={brightenImg2} alt="" />
            </div>
            <div className="brighten-assets__all brighten-assets__7">
              <img src={brightenImg3} alt="" />
            </div>
            <div className="brighten-assets__all brighten-assets__8">
              <img src={brightenImg4} alt="" />
            </div>
            <div className="brighten-assets__all brighten-assets__9">
              <img src={brightenImg5} alt="" />
            </div>
            <div className="brighten-assets__all brighten-assets__10">
              <img src={brightenImg6} alt="" />
            </div>
          </div>
          <div className="crystallise-assets">
            <div className="crystallise-assets__all crystallise-assets__1 crystallise-assets__dark">
              <span>🌱 Authority built</span>
            </div>
            <div className="crystallise-assets__all crystallise-assets__2 crystallise-assets__yellow">
              <span>✨ Smart exposure</span>
            </div>
            <div className="crystallise-assets__all crystallise-assets__3 crystallise-assets__dark">
              <span> 🔥 Seen + heard</span>
            </div>
            <div className="crystallise-assets__all crystallise-assets__4 crystallise-assets__green">
              <span>🎯 Right on cue</span>
            </div>
            <div className="crystallise-assets__all crystallise-assets__5 crystallise-assets__dark">
              <span>🤝 Community = currency</span>
            </div>
            <div className="crystallise-assets__all crystallise-assets__6 crystallise-assets__yellow">
              <span>💛 In the feels</span>
            </div>
            <div className="crystallise-assets__all crystallise-assets__7 crystallise-assets__red">
              <span>🧲 Pulled, not pushed</span>
            </div>
          </div>
        </div>
        <div className="home-bg-wrapper">
          <BgAnim />
        </div>
      </div>
    </section>
  );
}
